import NextLink from "next/link";
import {
  useRouter as useNextRouter,
  usePathname as useNextPathname,
} from "next/navigation";
import React from "react";

const LOCALES = ["en", "sl", "hr", "sk", "cs", "de-AT", "it", "pl"];

function getLocaleFromPathname(pathname: string): string {
  const segment = pathname.split("/")[1];
  return LOCALES.includes(segment) ? segment : "sl";
}

function prefixPath(path: string, locale: string): string {
  if (!path) return path;
  // External or special URLs — don't touch.
  if (/^[a-z]+:\/\//i.test(path) || path.startsWith("mailto:") || path.startsWith("tel:") || path.startsWith("#")) {
    return path;
  }
  // Split off query/hash for locale detection
  const pathPart = path.split("?")[0].split("#")[0];
  // Already prefixed with a known locale
  for (const l of LOCALES) {
    if (pathPart === `/${l}` || pathPart.startsWith(`/${l}/`)) return path;
  }
  return `/${locale}${path.startsWith("/") ? path : `/${path}`}`;
}

export function useRouter() {
  const pathname = useNextPathname();
  const locale = getLocaleFromPathname(pathname ?? "/sl");
  const router = useNextRouter();

  // Memoized so the wrapper is as stable as Next's own router. Pages list `router`
  // in effect deps; a fresh object per render re-ran the login page's mount check
  // after every state change, and once the login cookie was set that check pushed
  // home on top of `?to=` (the photographer-invite / dogodki return path was lost).
  return React.useMemo(
    () => ({
      ...router,
      push(href: string, options?: Parameters<typeof router.push>[1]) {
        return router.push(prefixPath(href, locale), options);
      },
      replace(href: string, options?: Parameters<typeof router.replace>[1]) {
        return router.replace(prefixPath(href, locale), options);
      },
      prefetch(href: string, options?: Parameters<typeof router.prefetch>[1]) {
        return router.prefetch(prefixPath(href, locale), options);
      },
    }),
    [router, locale],
  );
}

export function usePathname() {
  return useNextPathname();
}

type NextLinkProps = React.ComponentProps<typeof NextLink>;

// Locale-aware <Link> that auto-prefixes the current locale onto relative hrefs,
// mirroring next-intl's Link API without pulling in the full routing module.
export const Link = React.forwardRef<HTMLAnchorElement, NextLinkProps>(
  function LocaleAwareLink({ href, ...rest }, ref) {
    const pathname = useNextPathname();
    const locale = getLocaleFromPathname(pathname ?? "/sl");
    const resolvedHref =
      typeof href === "string" ? prefixPath(href, locale) : href;
    return <NextLink ref={ref} href={resolvedHref} {...rest} />;
  }
);
